import { EditPanel } from './types';

/** One panel group per stable roof face, independent of roof-template welds. */
export type PanelSelection =
  | { kind: 'group'; faceId: number }
  | { kind: 'panels'; faceId: number; indexes: number[]; anchorIndex?: number }
  | null;

const rectangularPanelIndexes = (
  panels: EditPanel[],
  anchor: EditPanel,
  clicked: EditPanel
): number[] => {
  const [origin, next, , previous] = anchor.corners;
  const u = { x: next.lng - origin.lng, y: next.lat - origin.lat };
  const v = { x: previous.lng - origin.lng, y: previous.lat - origin.lat };
  const determinant = u.x * v.y - u.y * v.x;
  if (determinant === 0) return [];

  // Resolve centres in the panel's own axes, including rotated/projected arrays.
  const centre = (panel: EditPanel) => {
    const x = panel.corners.reduce(
      (sum, corner) => sum + (corner.lng - origin.lng) / 4,
      0
    );
    const y = panel.corners.reduce(
      (sum, corner) => sum + (corner.lat - origin.lat) / 4,
      0
    );
    return {
      u: (x * v.y - y * v.x) / determinant,
      v: (u.x * y - u.y * x) / determinant,
    };
  };
  const start = centre(anchor);
  const end = centre(clicked);
  // Coordinate rounding must not exclude panels on the rectangle's boundary.
  const tolerance = 1e-5;
  return panels.flatMap((panel, index) => {
    if (panel.faceId !== anchor.faceId) return [];
    const point = centre(panel);
    return point.u >= Math.min(start.u, end.u) - tolerance &&
      point.u <= Math.max(start.u, end.u) + tolerance &&
      point.v >= Math.min(start.v, end.v) - tolerance &&
      point.v <= Math.max(start.v, end.v) + tolerance
      ? [index]
      : [];
  });
};

export const selectPanel = (
  selection: PanelSelection,
  panels: EditPanel[],
  index: number,
  additive: boolean,
  range: boolean = false
): PanelSelection => {
  const panel = panels[index];
  if (!panel) return selection;
  if (selection?.faceId !== panel.faceId) {
    return { kind: 'group', faceId: panel.faceId };
  }
  if (range && selection.kind === 'panels') {
    const anchorIndex = selection.anchorIndex ?? selection.indexes.at(-1);
    const anchor = anchorIndex === undefined ? undefined : panels[anchorIndex];
    if (anchor?.faceId === panel.faceId) {
      const block = rectangularPanelIndexes(panels, anchor, panel);
      if (block.length > 0) {
        return {
          kind: 'panels',
          faceId: panel.faceId,
          indexes: additive
            ? [
                ...new Set([
                  ...panelSelectionIndexes(selection, panels),
                  ...block,
                ]),
              ]
            : block,
          anchorIndex,
        };
      }
    }
  }
  let indexes = [index];
  if (additive && selection.kind === 'panels') {
    indexes = selection.indexes.includes(index)
      ? selection.indexes.filter((selected) => selected !== index)
      : [...selection.indexes, index];
  }
  return { kind: 'panels', faceId: panel.faceId, indexes, anchorIndex: index };
};

export const panelSelectionIndexes = (
  selection: PanelSelection,
  panels: EditPanel[]
): number[] => {
  if (!selection) return [];
  if (selection.kind === 'panels') {
    return selection.indexes.filter(
      (index) => panels[index]?.faceId === selection.faceId
    );
  }
  return panels.flatMap((panel, index) =>
    panel.faceId === selection.faceId ? [index] : []
  );
};

export const moveSelectedPanels = (
  panels: EditPanel[],
  indexes: number[],
  delta: { dLat: number; dLng: number },
  removedIndexes: number[] = []
): EditPanel[] => {
  const selected = new Set(indexes);
  const removed = new Set(removedIndexes);
  return panels.flatMap<EditPanel>((panel, index) => {
    if (!selected.has(index)) return [panel];
    if (removed.has(index)) return [];
    const shift = (corner: EditPanel['corners'][number]) => ({
      lat: corner.lat + delta.dLat,
      lng: corner.lng + delta.dLng,
    });
    return [
      {
        ...panel,
        corners: [
          shift(panel.corners[0]),
          shift(panel.corners[1]),
          shift(panel.corners[2]),
          shift(panel.corners[3]),
        ],
      },
    ];
  });
};
